import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  Image,
  ImageBackground,
  RefreshControl,
  Modal,
  Pressable,
  ActivityIndicator,
  TextInput,
  Platform,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GestureDetector, type NativeGesture } from 'react-native-gesture-handler';
import Icon from 'react-native-vector-icons/Feather';
import LinearGradient from 'react-native-linear-gradient';
import {
  doc,
  updateDoc,
  deleteDoc,
  arrayRemove,
  getDoc,
} from 'firebase/firestore';
import { usePlans, useProfile } from '../hooks/useProfile';
import { usePlanJoinRequest, useUserPendingPlanRequests } from '../hooks/usePlanJoinRequest';
import TonyteBackground from '../components/TonyteBackground';
import PlanTemplateCarousel from '../components/PlanTemplateCarousel';
import TonyteModal from '../components/TonyteModal';
import type { PlanTemplate } from '../data/planTemplates';
import { Colors, Typography, Spacing, Radius, Shadow } from '../theme';
import type { TonyteActivity, RootStackParamList } from '../types';
import { firestore, FIRESTORE_COLLECTIONS } from '../firebase';
import { TONYTE_PROFILE_IMAGE, getUserDisplayName, useUserProfile } from '../utils/userProfiles';
import { useCityContext } from '../context/CityContext';
import { canAccessCity, normalizeCityKey } from '../utils/cities';
import { useI18n } from '../i18n';
import { useTranslatedActivity } from '../hooks/useTranslatedActivity';
import { ANALYTICS_SOURCES, trackEventDetailOpen } from '../utils/eventAnalytics';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { getPlanMemberCount, isPlanMember } from '../utils/planMembership';
import { daylightStyle } from '../theme/daylightStyle';

const TRANSPARENT_GRADIENT: [string, string, string] = [
  'rgba(255,255,255,0)',
  'rgba(255,255,255,0)',
  'rgba(255,255,255,0)',
];

// ─── Risk logic ──────────────────────────────────────────────────────────────

const HIGH_RISK_WORDS = ['private', 'house', 'home', 'secret', 'drugs', 'weed', 'cash only', 'no police'];
const MEDIUM_RISK_WORDS = ['party', 'afterparty', 'drinks', 'alcohol', 'club', 'bar', 'night'];

type RiskLevel = 'low' | 'medium' | 'high';

function getRiskConfig(t: (k: string) => string): Record<RiskLevel, { color: string; label: string; subtext: string }> {
  return {
    low:    { color: '#34D399', label: t('event.lowRisk'),  subtext: t('event.lowRiskSub') },
    medium: { color: '#FBBF24', label: t('event.caution'),  subtext: t('event.cautionSub') },
    high:   { color: '#F87171', label: t('event.highRisk'), subtext: t('event.highRiskSub') },
  };
}

function getPlanRiskLevel(plan: TonyteActivity): RiskLevel {
  const text = `${plan.title} ${plan.description ?? ''}`.toLowerCase();
  if (HIGH_RISK_WORDS.some(w => text.includes(w))) return 'high';
  if (MEDIUM_RISK_WORDS.some(w => text.includes(w))) return 'medium';
  return 'low';
}

function getImageUrl(plan: TonyteActivity): string | null {
  return (
    plan.coverImageUrl ||
    plan.coverPhoto ||
    plan.imageUrl ||
    plan.image ||
    plan.photoUrl ||
    plan.coverUrl ||
    plan.posterUrl ||
    plan.mediaUrl ||
    null
  );
}

function formatPlanDate(dateStr: string, locale = 'en-US'): string {
  if (!dateStr) return '';
  const d = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
}

const hasRealProfilePhoto = (profile?: { photo?: string; profilePhotoURL?: string; profileImage?: string; photoPath?: string; profileImagePath?: string; profilePhotoPath?: string } | null) => {
  const photo = profile?.profilePhotoURL || profile?.profileImage || profile?.photo || '';
  const path = profile?.profileImagePath || profile?.photoPath || profile?.profilePhotoPath || '';
  return Boolean(
    path.trim() ||
    photo.includes('firebasestorage.googleapis.com') ||
    photo.includes('/profileImages/') ||
    photo.includes('/users/'),
  );
};

// ─── PlanRiskBar ─────────────────────────────────────────────────────────────

const PlanRiskBar: React.FC<{ plan: TonyteActivity; onLightCard?: boolean }> = ({ plan, onLightCard }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const risk = getPlanRiskLevel(plan);
  const cfg = getRiskConfig(t)[risk];
  return (
    <View style={[styles.riskBar, onLightCard && { backgroundColor: theme.colors.chipBackground, borderColor: theme.colors.border }]}>
      <View style={[styles.riskColorBar, { backgroundColor: cfg.color }]} />
      <View style={styles.riskContent}>
        <Text style={[styles.riskLabel, { color: cfg.color }]}>{cfg.label}</Text>
        <Text style={[styles.riskSubtext, onLightCard && { color: theme.colors.textSecondary }]}>{cfg.subtext}</Text>
      </View>
    </View>
  );
};

// ─── Safety modal ─────────────────────────────────────────────────────────────

interface SafetyModalProps {
  plan: TonyteActivity | null;
  highRiskAck: boolean;
  requestMessage: string;
  onToggleAck: () => void;
  onChangeMessage: (v: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  sending?: boolean;
}

const SafetyModal: React.FC<SafetyModalProps> = ({
  plan, highRiskAck, requestMessage, onToggleAck, onChangeMessage, onConfirm, onCancel, sending,
}) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  const translatedPlan = useTranslatedActivity(plan);
  if (!plan) return null;
  const risk = getPlanRiskLevel(plan);
  const cfg = getRiskConfig(t)[risk];
  const isHigh = risk === 'high';
  const confirmDisabled = (isHigh && !highRiskAck) || Boolean(sending);

  return (
    <Modal transparent animationType="fade" visible statusBarTranslucent onRequestClose={sending ? undefined : onCancel}>
      <Pressable style={styles.safetyOverlay} onPress={sending ? undefined : onCancel}>
        <Pressable style={[styles.safetyCard, themedStyles.modalCard]} onPress={() => {}}>
          <Text style={[styles.safetyTitle, themedStyles.textPrimary]}>{t('event.requestToJoin')}</Text>
          <Text style={[styles.safetyPlanName, themedStyles.textSecondary]} numberOfLines={2}>{translatedPlan.translatingTitle ? '...' : (translatedPlan.title || plan.title)}</Text>

          <View style={[styles.riskBar, { marginBottom: Spacing.base }]}>
            <View style={[styles.riskColorBar, { backgroundColor: cfg.color }]} />
            <View style={styles.riskContent}>
              <Text style={[styles.riskLabel, { color: cfg.color }]}>{cfg.label}</Text>
              <Text style={[styles.riskSubtext, themedStyles.textMuted]}>{cfg.subtext}</Text>
            </View>
          </View>

          {risk !== 'low' && (
            <View style={[styles.safetyWarning, { borderColor: `${cfg.color}44` }]}>
              <Icon name={isHigh ? 'alert-triangle' : 'alert-circle'} size={16} color={cfg.color} />
              <Text style={[styles.safetyWarningText, { color: cfg.color }]}>
                {isHigh ? t('event.highRiskWarning') : t('event.mediumRiskWarning')}
              </Text>
            </View>
          )}

          {isHigh && (
            <TouchableOpacity style={styles.ackRow} onPress={onToggleAck} activeOpacity={0.8}>
              <View style={[styles.ackCheckbox, highRiskAck && styles.ackCheckboxChecked]}>
                {highRiskAck && <Icon name="check" size={12} color="#fff" />}
              </View>
              <Text style={styles.ackText}>{t('event.ackRisks')}</Text>
            </TouchableOpacity>
          )}

          <TextInput
            style={[styles.requestMsgInput, themedStyles.input]}
            placeholder={t('event.messageOrganizerOptional')}
            placeholderTextColor={theme.colors.textMuted}
            value={requestMessage}
            onChangeText={onChangeMessage}
            multiline
            numberOfLines={2}
            maxLength={200}
            editable={!sending}
          />

          <View style={styles.safetyActions}>
            <TouchableOpacity style={[styles.safetyCancel, themedStyles.secondaryButton]} onPress={sending ? undefined : onCancel} activeOpacity={sending ? 1 : 0.8}>
              <Text style={[styles.safetyCancelText, themedStyles.textSecondary]}>{t('common.cancel')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.safetyConfirm, confirmDisabled && styles.safetyConfirmDisabled]}
              onPress={confirmDisabled ? undefined : onConfirm}
              activeOpacity={confirmDisabled ? 1 : 0.8}
            >
              {sending
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={styles.safetyConfirmText}>{t('event.sendRequest')}</Text>
              }
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

// ─── Admin Leave Modal ────────────────────────────────────────────────────────

interface AdminLeaveModalProps {
  plan: TonyteActivity | null;
  deleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const AdminLeaveModal: React.FC<AdminLeaveModalProps> = ({ plan, deleting, onConfirm, onCancel }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  const translatedPlan = useTranslatedActivity(plan);
  if (!plan) return null;
  return (
    <Modal transparent animationType="fade" visible statusBarTranslucent onRequestClose={deleting ? undefined : onCancel}>
      <Pressable style={styles.safetyOverlay} onPress={deleting ? undefined : onCancel}>
        <Pressable style={[adminStyles.card, themedStyles.modalCard]} onPress={() => {}}>
          <View style={adminStyles.iconRow}>
            <View style={adminStyles.warningIcon}>
              <Icon name="alert-triangle" size={20} color="#F87171" />
            </View>
          </View>
          <Text style={[adminStyles.title, themedStyles.textPrimary]}>{t('event.adminCannotLeave')}</Text>
          <Text style={[adminStyles.subtitle, themedStyles.textSecondary]}>{t('event.deletePlanInstead')}</Text>
          <Text style={[adminStyles.planHint, themedStyles.textMuted]} numberOfLines={2}>{translatedPlan.translatingTitle ? '...' : (translatedPlan.title || plan.title)}</Text>
          <View style={styles.safetyActions}>
            <TouchableOpacity
              style={[styles.safetyCancel, themedStyles.secondaryButton]}
              onPress={deleting ? undefined : onCancel}
              activeOpacity={deleting ? 1 : 0.8}
            >
              <Text style={[styles.safetyCancelText, themedStyles.textSecondary]}>{t('event.noStay')}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[adminStyles.deleteBtn, deleting && adminStyles.deleteBtnLoading]}
              onPress={deleting ? undefined : onConfirm}
              activeOpacity={deleting ? 1 : 0.8}
            >
              {deleting
                ? <ActivityIndicator size="small" color="#fff" />
                : <Text style={adminStyles.deleteBtnText}>{t('event.yesDeletePlan')}</Text>
              }
            </TouchableOpacity>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

const adminStyles = StyleSheet.create({
  card: {
    width: '100%',
    backgroundColor: '#0f1520',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: Radius.xxl,
    padding: Spacing.xl,
    gap: Spacing.md,
  },
  iconRow: {
    alignItems: 'center',
    marginBottom: 4,
  },
  warningIcon: {
    width: 46,
    height: 46,
    borderRadius: 23,
    backgroundColor: 'rgba(248,113,113,0.12)',
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.28)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    color: '#ffffff',
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
    textAlign: 'center',
  },
  subtitle: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: Typography.base,
    textAlign: 'center',
  },
  planHint: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: Typography.sm,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  deleteBtn: {
    flex: 1,
    backgroundColor: '#F87171',
    borderRadius: Radius.md,
    paddingVertical: Spacing.md,
    alignItems: 'center',
    shadowColor: '#F87171',
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.35,
    shadowRadius: 10,
    elevation: 6,
  },
  deleteBtnLoading: {
    opacity: 0.65,
    shadowOpacity: 0,
    elevation: 0,
  },
  deleteBtnText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
});

// ─── Plan card ────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: TonyteActivity;
  userId: string | null;
  canDelete: boolean;
  onJoin: (plan: TonyteActivity) => void;
  onLeave: (plan: TonyteActivity) => void;
  onOpen: (plan: TonyteActivity) => void;
  onDelete: (plan: TonyteActivity) => void;
  joining: string | null;
  hasPendingRequest?: boolean;
}

const LOCALE_MAP: Record<string, string> = { de: 'de-DE', es: 'es-ES', fr: 'fr-FR', ru: 'ru-RU' };

const PlanCard: React.FC<PlanCardProps> = React.memo(({ plan, userId, canDelete, onJoin, onLeave, onOpen, onDelete, joining, hasPendingRequest }) => {
  const { t, tp, language } = useI18n();
  const { theme } = useTheme();
  const { width } = useWindowDimensions();
  const isDesktopWeb = Platform.OS === 'web' && width >= 1024;
  const locale = LOCALE_MAP[language] ?? 'en-US';
  const translatedPlan = useTranslatedActivity(plan);
  const imageUrl = getImageUrl(plan);
  const creatorProfile = useUserProfile(plan.createdBy || plan.hostId);
  const [creatorImageFailed, setCreatorImageFailed] = useState(false);
  const isJoined = isPlanMember(plan, userId);
  const memberCount = getPlanMemberCount(plan);
  const dateStr = plan.startDate ? formatPlanDate(plan.startDate, locale) : '';
  const isProcessing = joining === plan.id;
  const creatorImage = creatorProfile?.profileImage || '';
  const creatorName = creatorProfile ? getUserDisplayName(creatorProfile) : plan.hostName || 'User';

  React.useEffect(() => {
    setCreatorImageFailed(false);
  }, [creatorImage]);

  // The dark overlay exists to keep light text legible over an arbitrary,
  // uncontrolled PHOTO — it has no reason to exist over the fallback
  // gradient, which is a deliberately-chosen, already-legible background.
  // Applying it there just muddies a clean daylight pastel into gray sludge.
  const hasImage = Boolean(imageUrl);

  if (isDesktopWeb) {
    return (
      <TouchableOpacity style={[styles.desktopPlanCard, { backgroundColor: theme.colors.cardBackground, borderColor: theme.colors.cardBorder }]} onPress={() => onOpen(plan)} activeOpacity={0.9}>
        {imageUrl ? <Image source={{ uri: imageUrl }} style={styles.desktopPlanImage} resizeMode="cover" /> : <LinearGradient colors={theme.eventCard.fallbackGradient} style={styles.desktopPlanImage} />}
        <View style={styles.desktopPlanBody}>
          <Text style={[styles.desktopPlanTitle, { color: theme.colors.textPrimary }]} numberOfLines={1}>{translatedPlan.translatingTitle ? '...' : (translatedPlan.title || plan.title)}</Text>
          <View style={styles.desktopPlanMetaRow}><Icon name="calendar" size={12} color={theme.colors.iconSubtle} /><Text style={[styles.desktopPlanMeta, { color: theme.colors.textSecondary }]} numberOfLines={1}>{dateStr}{plan.startTime ? `, ${plan.startTime}` : ''}</Text></View>
          <View style={styles.desktopPlanMetaRow}><Icon name="map-pin" size={12} color={theme.colors.iconSubtle} /><Text style={[styles.desktopPlanMeta, { color: theme.colors.textSecondary }]} numberOfLines={1}>{plan.locationName || plan.venueName || plan.address || plan.city}</Text></View>
          <View style={styles.desktopPlanFooter}>
            <View style={styles.desktopHost}><View style={styles.desktopHostAvatarWrap}>{creatorImage && !creatorImageFailed ? <Image source={{ uri: creatorImage }} style={styles.desktopHostAvatar} onError={() => setCreatorImageFailed(true)} /> : <Image source={TONYTE_PROFILE_IMAGE} style={styles.desktopHostAvatar} />}</View><Text style={[styles.desktopHostName, { color: theme.colors.textPrimary }]} numberOfLines={1}>{creatorName}</Text></View>
            <Text style={[styles.desktopGoing, { color: theme.colors.textSecondary }]}>{tp('event.memberCount', memberCount)}</Text>
            <TouchableOpacity onPress={(event) => { event.stopPropagation(); onOpen(plan); }} style={styles.desktopViewPlan}><Text style={styles.desktopViewPlanText}>{t('plans.open')}</Text></TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
    );
  }

  const overlay = (
    <>
      <LinearGradient
        colors={theme.eventCard.imageOverlay}
        locations={[0, 0.38, 0.62, 1]}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
      <LinearGradient
        colors={['rgba(0,0,0,0.46)', 'rgba(0,0,0,0.04)', 'rgba(0,0,0,0.04)', 'rgba(0,0,0,0.46)']}
        locations={[0, 0.22, 0.78, 1]}
        start={{ x: 0, y: 0.5 }}
        end={{ x: 1, y: 0.5 }}
        style={StyleSheet.absoluteFill}
        pointerEvents="none"
      />
    </>
  );

  const cardContent = (
    <>
      {hasImage ? overlay : null}

      {/* Top badges + admin delete */}
      <View style={styles.cardBadgesRow}>
        <View style={styles.communityBadge}>
          <Text style={styles.communityBadgeText}>{t('plans.communityPlanBadge')}</Text>
        </View>
        {plan.isFree && (
          <View
            style={[
              styles.freeBadge,
              !hasImage && { backgroundColor: theme.eventCard.chipBackground, borderColor: theme.eventCard.chipBorder },
            ]}
          >
            <Text style={[styles.freeBadgeText, !hasImage && { color: theme.eventCard.chipText }]}>{t('plans.freeBadge')}</Text>
          </View>
        )}
        {canDelete && (
          <TouchableOpacity
            style={styles.deleteBtn}
            onPress={() => onDelete(plan)}
            activeOpacity={0.8}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            <Icon name="trash-2" size={14} color="#F87171" />
          </TouchableOpacity>
        )}
      </View>

      {/* Bottom content block */}
      <View style={styles.cardBottom}>
        {dateStr ? (
          <View style={styles.dateRow}>
            <Icon name="calendar" size={11} color="#26D0CE" />
            <Text style={styles.dateText}>{dateStr}</Text>
          </View>
        ) : null}

        <Text style={[styles.cardTitle, !hasImage && { color: theme.colors.textPrimary }]} numberOfLines={2}>{translatedPlan.translatingTitle ? '...' : (translatedPlan.title || plan.title)}</Text>

        {(translatedPlan.description || plan.description) ? (
          <Text style={[styles.cardDesc, !hasImage && { color: theme.colors.textSecondary }]} numberOfLines={2}>{translatedPlan.translatingDescription ? '...' : (translatedPlan.description || plan.description)}</Text>
        ) : null}

        <PlanRiskBar plan={plan} onLightCard={!hasImage} />

        {/* Creator / action card */}
        <View style={[styles.creatorCard, { backgroundColor: theme.eventCard.manageBackground, borderColor: theme.eventCard.manageBorder }]}>
          <View style={styles.creatorTop}>
            <View style={styles.creatorAvatarFrame}>
              {creatorImage && !creatorImageFailed ? (
                <Image
                  source={{ uri: creatorImage }}
                  style={styles.creatorAvatar}
                  resizeMode="cover"
                  onError={() => setCreatorImageFailed(true)}
                />
              ) : (
                <Image source={TONYTE_PROFILE_IMAGE} style={styles.creatorAvatar} resizeMode="cover" />
              )}
            </View>
            <View style={styles.creatorInfo}>
              <Text style={[styles.creatorLabel, { color: theme.eventCard.manageText }]} numberOfLines={1}>{t('plans.postedBy')} {creatorName}</Text>
              <Text style={[styles.memberCount, { color: theme.eventCard.bottomMutedText }]}>{tp('event.memberCount', memberCount)}</Text>
            </View>
          </View>
          <View style={styles.creatorBtns}>
            <TouchableOpacity
              style={[styles.joinBtn, (isJoined || hasPendingRequest) && styles.joinedBtn]}
              onPress={() => (isJoined ? onLeave(plan) : !hasPendingRequest ? onJoin(plan) : undefined)}
              disabled={isProcessing || hasPendingRequest}
              activeOpacity={hasPendingRequest ? 1 : 0.8}
            >
              <Text style={[styles.joinBtnText, (isJoined || hasPendingRequest) && styles.joinedBtnText]}>
                {isProcessing ? '...' : isJoined ? t('plans.leave') : hasPendingRequest ? t('event.requestSentShort') : t('plans.request')}
              </Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.openBtn, { backgroundColor: theme.eventCard.chipBackground, borderColor: theme.eventCard.chipBorder }]}
              onPress={() => onOpen(plan)}
              activeOpacity={0.8}
            >
              <Text style={[styles.openBtnText, { color: theme.eventCard.chipText }]}>{t('plans.open')}</Text>
              <Icon name="arrow-right" size={13} color={theme.eventCard.chipText} />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </>
  );

  if (imageUrl) {
    return (
      <ImageBackground
        source={{ uri: imageUrl }}
        style={[styles.card, isDesktopWeb && styles.desktopCard]}
        imageStyle={{ borderRadius: Radius.xxl }}
        resizeMode="cover"
      >
        {cardContent}
      </ImageBackground>
    );
  }

  return (
    <LinearGradient colors={theme.eventCard.fallbackGradient} style={[styles.card, isDesktopWeb && styles.desktopCard]}>
      {cardContent}
    </LinearGradient>
  );
});

// ─── Empty state ──────────────────────────────────────────────────────────────

const PlansEmptyState: React.FC<{ onCreatePlan: () => void }> = ({ onCreatePlan }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  return (
    <View style={[styles.emptyWrap, Platform.OS === 'ios' && styles.listEmptyWrapIOS]}>
      <View style={[styles.emptyCard, Platform.OS === 'ios' && styles.emptyCardIOS, themedStyles.emptyCard]}>
        {theme.isDark ? (
          <LinearGradient
            pointerEvents="none"
            colors={TRANSPARENT_GRADIENT}
            locations={[0, 0.52, 1]}
            start={{ x: 0.2, y: 0 }}
            end={{ x: 0.8, y: 1 }}
            style={styles.emptyCardGradient}
          />
        ) : null}
        <View pointerEvents="none" style={[styles.emptyTopHighlight, themedStyles.emptyTopHighlight]} />
        <Icon name="calendar" size={32} color={theme.colors.iconFaint} />
        <Text style={[styles.emptyTitle, themedStyles.textPrimary]}>{t('plans.noneYet')}</Text>
        <Text style={[styles.emptySubtitle, themedStyles.textMuted]}>{t('plans.noneYetSubtitle')}</Text>
        <TouchableOpacity style={styles.createBtn} onPress={onCreatePlan} activeOpacity={0.85}>
          <Icon name="plus" size={15} color="#fff" />
          <Text style={styles.createBtnText}>{t('plans.createPlan')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

// ─── Main screen ──────────────────────────────────────────────────────────────

interface Props {
  userId: string | null;
  scrollGesture?: NativeGesture;
}

const PlansScreen: React.FC<Props> = ({ userId, scrollGesture }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themedStyles = useMemo(() => createThemeStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  // The desktop reference is a stable three-card gallery. Keeping each item
  // on a fixed third of the row also prevents a lone final card from growing
  // into one long, full-width plan.
  const desktopColumns = Platform.OS === 'web' && width >= 1024 ? 3 : 1;
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const { plans, loading } = usePlans(userId);
  const { selectedCityKey } = useCityContext();
  const { profile } = useProfile(userId);

  const cityFilteredPlans = useMemo(() => plans.filter(p =>
    canAccessCity(normalizeCityKey(p.cityKey || p.city), profile?.role) &&
    (!selectedCityKey || normalizeCityKey(p.cityKey || p.city) === selectedCityKey),
  ), [plans, profile?.role, selectedCityKey]);
  const isAdmin = profile?.role === 'admin' || profile?.role === 'co-admin';
  const [refreshing, setRefreshing] = useState(false);
  const [safetyPlan, setSafetyPlan] = useState<TonyteActivity | null>(null);
  const [highRiskAck, setHighRiskAck] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [sendingRequest, setSendingRequest] = useState(false);
  const [joining, setJoining] = useState<string | null>(null);
  const [adminLeavePlan, setAdminLeavePlan] = useState<TonyteActivity | null>(null);
  const [deletingPlan, setDeletingPlan] = useState(false);
  const [planPendingDelete, setPlanPendingDelete] = useState<TonyteActivity | null>(null);
  // App-themed info/error dialog (TonyteModal) instead of the system Alert.
  // Opened a moment later so it never collides with a modal that is still
  // closing (iOS can't present one modal while another is dismissing).
  const [notice, setNotice] = useState<{ title: string; message: string; tone: 'success' | 'error' } | null>(null);
  const showNotice = useCallback((title: string, message: string, tone: 'success' | 'error' = 'success') => {
    setTimeout(() => setNotice({ title, message, tone }), 300);
  }, []);

  const { sendRequest } = usePlanJoinRequest(safetyPlan?.id ?? null, userId);
  const pendingPlanIds = useUserPendingPlanRequests(userId);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => setRefreshing(false), 800);
  }, []);

  const handleJoin = useCallback((plan: TonyteActivity) => {
    if (!userId) {
      showNotice(t('auth.signInRequired'), t('plans.signInRequiredSubtitle'), 'error');
      return;
    }
    setHighRiskAck(false);
    setRequestMessage('');
    setSafetyPlan(plan);
  }, [showNotice, userId, t]);

  const handleLeave = useCallback(async (plan: TonyteActivity) => {
    if (!userId) return;
    if (plan.hostId === userId || plan.createdBy === userId) {
      setAdminLeavePlan(plan);
      return;
    }
    setJoining(plan.id);
    try {
      const existingAttendee = plan.attendees?.find(a => a.id === userId || (a as any).userId === userId);
      const planRef = doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id);
      const snap = await getDoc(planRef);
      const currentCount = typeof snap.data()?.participants === 'number' ? (snap.data()!.participants as number) : 0;
      await updateDoc(planRef, {
        participantIds: arrayRemove(userId),
        members: arrayRemove(userId),
        ...(existingAttendee ? { attendees: arrayRemove(existingAttendee) } : {}),
        participants: Math.max(currentCount - 1, 0),
      });
      await updateDoc(doc(firestore, FIRESTORE_COLLECTIONS.planChats, plan.id), {
        memberIds: arrayRemove(userId),
      });
      deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id, 'participants', userId)).catch(() => {});
    } catch {
      showNotice(t('common.error'), t('plans.leaveFailed'), 'error');
    } finally {
      setJoining(null);
    }
  }, [showNotice, userId, t]);

  const handleAdminDelete = useCallback(async () => {
    if (!adminLeavePlan || deletingPlan) return;
    const plan = adminLeavePlan;
    setDeletingPlan(true);
    try {
      await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id));
      try {
        await deleteDoc(doc(firestore, 'planChats', plan.id));
      } catch {
        // planChat may not exist
      }
      setAdminLeavePlan(null);
      setDeletingPlan(false);
      showNotice(t('plans.planDeleted'), `"${plan.title}" ${t('plans.removed')}`);
    } catch {
      setDeletingPlan(false);
      showNotice(t('common.error'), t('plans.deleteFailed'), 'error');
    }
  }, [adminLeavePlan, deletingPlan, showNotice, t]);

  const handleConfirmJoin = useCallback(async () => {
    if (!safetyPlan || !userId) return;
    const plan = safetyPlan;
    const msg = requestMessage;
    setSendingRequest(true);
    try {
      await sendRequest(plan, msg);
      setSafetyPlan(null);
      setRequestMessage('');
      showNotice(t('plans.requestSent'), t('plans.requestSentSubtitle'));
    } catch {
      showNotice(t('common.error'), t('plans.requestFailed'), 'error');
    } finally {
      setSendingRequest(false);
    }
  }, [safetyPlan, userId, requestMessage, sendRequest, showNotice, t]);

  const handleOpen = useCallback((plan: TonyteActivity) => {
    trackEventDetailOpen({ eventId: plan.id, organizerId: plan.hostId, source: ANALYTICS_SOURCES.PLAN });
    navigation.navigate('EventDetail', { eventId: plan.id, event: plan });
  }, [navigation]);

  const handleCreatePlan = useCallback(() => {
    navigation.navigate('UploadPlan');
  }, [navigation]);

  const handleSelectTemplate = useCallback((template: PlanTemplate) => {
    navigation.navigate('UploadPlan', {
      initialValues: {
        title: template.title,
        description: template.description,
        location: template.location,
        participantLimit: template.participantLimit,
      },
    });
  }, [navigation]);

  const handleDeletePlan = useCallback((plan: TonyteActivity) => {
    setPlanPendingDelete(plan);
  }, []);

  const renderItem = useCallback(({ item }: { item: TonyteActivity }) => {
    const canDelete = isAdmin || item.hostId === userId || item.createdBy === userId;
    return (
      <View style={desktopColumns > 1 ? styles.desktopPlanItem : undefined}>
      <PlanCard
        plan={item}
        userId={userId}
        canDelete={canDelete}
        onJoin={handleJoin}
        onLeave={handleLeave}
        onOpen={handleOpen}
        onDelete={handleDeletePlan}
        joining={joining}
        hasPendingRequest={pendingPlanIds.has(item.id ?? '')}
      />
      </View>
    );
  }, [desktopColumns, isAdmin, userId, handleJoin, handleLeave, handleOpen, handleDeletePlan, joining, pendingPlanIds]);

  if (loading && !refreshing) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, themedStyles.container, { paddingTop: insets.top }]}> 
        <View style={styles.loadingWrap}>
          <Text style={[styles.loadingText, themedStyles.textMuted]}>{t('plans.loading')}</Text>
        </View>
      </TonyteBackground>
    );
  }

  if (profile && !hasRealProfilePhoto(profile)) {
    return (
      <TonyteBackground appearanceAware style={[styles.container, themedStyles.container, { paddingTop: insets.top }]}> 
        <View style={styles.header}>
          <View>
            <Text style={[styles.headerTitle, themedStyles.textPrimary]}>{t('plans.communityPlans')}</Text>
            <Text style={[styles.headerSub, themedStyles.textMuted]}>{t('plans.communityPlansSubtitle')}</Text>
          </View>
        </View>
        <View style={styles.emptyWrap}>
          <View style={[styles.emptyCard, Platform.OS === 'ios' && styles.emptyCardIOS, themedStyles.emptyCard]}>
            {theme.isDark ? (
              <LinearGradient
                pointerEvents="none"
                colors={TRANSPARENT_GRADIENT}
                locations={[0, 0.52, 1]}
                start={{ x: 0.2, y: 0 }}
                end={{ x: 0.8, y: 1 }}
                style={styles.emptyCardGradient}
              />
            ) : null}
            <View pointerEvents="none" style={[styles.emptyTopHighlight, themedStyles.emptyTopHighlight]} />
            <Icon name="lock" size={32} color={theme.colors.accentSecondary} />
            <Text style={[styles.emptyTitle, themedStyles.textPrimary]}>{t('plans.completeProfile')}</Text>
            <TouchableOpacity
              style={[styles.profilePhotoRequirement, themedStyles.profilePhotoRequirement]}
              onPress={() => navigation.navigate('CompleteProfile', {
                initialStep: 'profile_photo',
                returnAfterProfilePhoto: true,
                autoOpenProfilePhotoPicker: true,
              })}
              activeOpacity={0.78}
              accessibilityRole="button"
              accessibilityLabel={t('plans.addRealProfilePicture')}
            >
              <Icon name="camera" size={17} color={theme.colors.accentSecondary} />
              <Text style={[styles.profilePhotoRequirementText, themedStyles.profilePhotoRequirementText]}>
                {t('plans.addRealProfilePicture')}
              </Text>
            </TouchableOpacity>
            <Text style={[styles.emptySubtitle, themedStyles.profilePhotoSupportingText]}>{t('plans.completeProfileSubtitle')}</Text>
          </View>
        </View>
      </TonyteBackground>
    );
  }

  const list = (
    <FlatList
      key={`plans-${desktopColumns}`}
      style={styles.scroller}
      data={cityFilteredPlans}
      scrollEnabled
      nestedScrollEnabled
      keyExtractor={item => item.id}
      renderItem={renderItem}
      numColumns={desktopColumns}
      columnWrapperStyle={desktopColumns > 1 ? styles.desktopPlanRow : undefined}
      contentContainerStyle={[styles.list, desktopColumns > 1 && styles.desktopList, { paddingBottom: desktopColumns > 1 ? 28 : insets.bottom + 100 }]}
      showsVerticalScrollIndicator={false}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={Colors.sunset} />
      }
      ListEmptyComponent={<PlansEmptyState onCreatePlan={handleCreatePlan} />}
      ListFooterComponent={<PlanTemplateCarousel onSelectTemplate={handleSelectTemplate} />}
    />
  );

  return (
    <TonyteBackground appearanceAware style={[styles.container, themedStyles.container, { paddingTop: insets.top }]}> 
      {/* Header */}
      <View style={[styles.header, desktopColumns > 1 && styles.desktopHeader]}>
        <View>
          <Text style={[styles.headerTitle, themedStyles.textPrimary]}>{t('plans.communityPlans')}</Text>
          <Text style={[styles.headerSub, themedStyles.textMuted]}>{t('plans.communityPlansSubtitle')}</Text>
        </View>
        <TouchableOpacity style={[styles.newBtn, desktopColumns > 1 && styles.desktopNewBtn, themedStyles.newButton]} onPress={handleCreatePlan} activeOpacity={0.8}>
          <View pointerEvents="none" style={[styles.newBtnHighlight, themedStyles.newBtnHighlight]} />
          <Icon name="plus" size={18} color={theme.colors.textPrimary} />
          {desktopColumns > 1 ? <Text style={[styles.desktopNewBtnText, themedStyles.textPrimary]}>{t('plans.createPlan')}</Text> : null}
        </TouchableOpacity>
      </View>

      {scrollGesture ? (
        <GestureDetector gesture={scrollGesture}>{list}</GestureDetector>
      ) : list}

      <SafetyModal
        plan={safetyPlan}
        highRiskAck={highRiskAck}
        requestMessage={requestMessage}
        onToggleAck={() => setHighRiskAck(v => !v)}
        onChangeMessage={setRequestMessage}
        onConfirm={handleConfirmJoin}
        onCancel={() => { setSafetyPlan(null); setRequestMessage(''); }}
        sending={sendingRequest}
      />
      <AdminLeaveModal
        plan={adminLeavePlan}
        deleting={deletingPlan}
        onConfirm={handleAdminDelete}
        onCancel={() => setAdminLeavePlan(null)}
      />
      <TonyteModal
        visible={Boolean(planPendingDelete)}
        title={t('plans.deletePlan')}
        message={planPendingDelete
          ? `${t('plans.deletePlanConfirmPrefix')} "${planPendingDelete.title}" ${t('plans.deletePlanConfirmSuffix')}`
          : ''}
        icon="trash-2"
        iconColor={Colors.sunset}
        buttons={[
          { label: t('common.cancel'), variant: 'secondary', onPress: () => setPlanPendingDelete(null) },
          {
            label: t('common.delete'),
            variant: 'danger',
            onPress: async () => {
              if (!planPendingDelete) return;
              const plan = planPendingDelete;
              setPlanPendingDelete(null);
              try {
                await deleteDoc(doc(firestore, FIRESTORE_COLLECTIONS.plans, plan.id));
              } catch {
                showNotice(t('common.error'), t('plans.deleteFailed'), 'error');
              }
            },
          },
        ]}
        onDismiss={() => setPlanPendingDelete(null)}
      />
      <TonyteModal
        visible={Boolean(notice)}
        title={notice?.title ?? ''}
        message={notice?.message}
        icon={notice?.tone === 'error' ? 'alert-circle' : 'check-circle'}
        iconColor={notice?.tone === 'error' ? Colors.sunset : Colors.turquoise}
        buttons={[{ label: t('common.ok'), variant: 'primary', onPress: () => setNotice(null) }]}
        onDismiss={() => setNotice(null)}
      />
    </TonyteBackground>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: { flex: 1 },
  loadingWrap: { flex: 1, alignItems: 'center', justifyContent: 'center' },
  loadingText: { color: Colors.textMuted, fontSize: Typography.sm },

  // Header
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.md,
    paddingBottom: Spacing.sm,
  },
  headerTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.xxl,
    fontWeight: Typography.extrabold,
  },
  headerSub: {
    color: 'rgba(255,255,255,0.40)',
    fontSize: 14,
    marginTop: 2,
  },
  newBtn: {
    width: 44,
    height: 44,
    borderRadius: Radius.md,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
  },
  newBtnHighlight: {
    position: 'absolute',
    left: 7,
    right: 7,
    top: 4,
    height: 1,
    borderRadius: 1,
    backgroundColor: 'transparent',
  },

  // List
  scroller: { flex: 1 },
  list: {
    paddingHorizontal: Spacing.base,
    paddingTop: Spacing.sm,
    gap: Spacing.base,
  },
  desktopHeader: { width: '100%', maxWidth: 1440, alignSelf: 'center', paddingHorizontal: 24, paddingTop: 18, paddingBottom: 8 },
  desktopList: { width: '100%', maxWidth: 1440, alignSelf: 'center', paddingHorizontal: 24, gap: 0 },
  desktopPlanRow: { gap: 16, marginBottom: 16 },
  desktopPlanItem: { width: '32%', flexGrow: 0, flexShrink: 1, minWidth: 0 },
  desktopCard: { minHeight: 330, borderRadius: 12 },
  desktopPlanCard: { height: 276, borderRadius: 12, borderWidth: 1, overflow: 'hidden' },
  desktopPlanImage: { width: '100%', height: 128 },
  desktopPlanBody: { flex: 1, padding: 13 },
  desktopPlanTitle: { fontSize: 15, lineHeight: 19, fontWeight: '700', marginBottom: 7 },
  desktopPlanMetaRow: { flexDirection: 'row', alignItems: 'center', gap: 7, marginBottom: 5 },
  desktopPlanMeta: { flex: 1, fontSize: 11, lineHeight: 14 },
  desktopPlanFooter: { marginTop: 'auto', flexDirection: 'row', alignItems: 'center', gap: 9 },
  desktopHost: { flex: 1, minWidth: 0, flexDirection: 'row', alignItems: 'center', gap: 7 },
  desktopHostAvatarWrap: { width: 30, height: 30, borderRadius: 15, overflow: 'hidden' },
  desktopHostAvatar: { width: 30, height: 30, borderRadius: 15 },
  desktopHostName: { flex: 1, fontSize: 11, fontWeight: '600' },
  desktopGoing: { fontSize: 10 },
  desktopViewPlan: { height: 30, paddingHorizontal: 11, borderRadius: 7, borderWidth: 1, borderColor: 'rgba(213,116,59,0.55)', alignItems: 'center', justifyContent: 'center' },
  desktopViewPlanText: { color: '#F58538', fontSize: 10, fontWeight: '700' },
  desktopNewBtn: { width: 'auto', minWidth: 124, paddingHorizontal: 15, flexDirection: 'row', gap: 7, backgroundColor: '#F58538', borderColor: '#F58538' },
  desktopNewBtnText: { fontSize: 12, fontWeight: '700' },

  // Plan card
  card: {
    minHeight: 360,
    borderRadius: Radius.xxl,
    overflow: 'hidden',
    justifyContent: 'space-between',
    ...Shadow.strong,
  },
  cardBadgesRow: {
    flexDirection: 'row',
    gap: Spacing.sm,
    padding: Spacing.base,
    paddingBottom: 0,
  },
  communityBadge: {
    backgroundColor: 'rgba(38,208,206,0.18)',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.30)',
    borderRadius: Radius.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  communityBadgeText: {
    color: '#26D0CE',
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 0.6,
  },
  freeBadge: {
    backgroundColor: 'rgba(255,255,255,0.14)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.22)',
    borderRadius: Radius.pill,
    paddingHorizontal: 10,
    paddingVertical: 4,
  },
  freeBadgeText: {
    color: '#ffffff',
    fontSize: 10,
    fontWeight: Typography.bold,
    letterSpacing: 0.6,
  },

  deleteBtn: {
    marginLeft: 'auto' as any,
    width: 30,
    height: 30,
    borderRadius: 10,
    backgroundColor: 'rgba(248,113,113,0.15)',
    borderWidth: 1,
    borderColor: 'rgba(248,113,113,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },

  // Card bottom
  cardBottom: {
    padding: Spacing.base,
    gap: Spacing.sm,
  },
  dateRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
  },
  dateText: {
    color: '#26D0CE',
    fontSize: Typography.xs,
    fontWeight: Typography.semibold,
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: 28,
    fontWeight: '900',
    lineHeight: 32,
  },
  cardDesc: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: 14,
    lineHeight: 20,
  },

  // Risk bar
  riskBar: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255,255,255,0.05)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.07)',
    borderRadius: Radius.md,
    paddingHorizontal: Spacing.base,
    paddingVertical: Spacing.md,
    gap: Spacing.md,
    overflow: 'hidden',
  },
  riskColorBar: {
    width: 3,
    height: '100%',
    borderRadius: 2,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  riskContent: {
    marginLeft: 10,
    gap: 2,
  },
  riskLabel: {
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
  },
  riskSubtext: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: Typography.xs,
  },

  // Creator card
  creatorCard: {
    backgroundColor: 'rgba(10,14,22,0.48)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: Radius.md,
    padding: Spacing.md,
    gap: Spacing.sm,
  },
  creatorTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
  },
  creatorAvatarFrame: {
    width: 34,
    height: 34,
    borderRadius: 17,
    padding: 1,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.35)',
    backgroundColor: 'rgba(255,255,255,0.08)',
    shadowColor: Colors.turquoise,
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.22,
    shadowRadius: 8,
    elevation: 4,
  },
  creatorAvatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: Colors.bgCardAlt,
  },
  creatorInfo: {
    flex: 1,
    gap: 2,
  },
  creatorLabel: {
    color: 'rgba(255,255,255,0.80)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },
  memberCount: {
    color: 'rgba(255,255,255,0.45)',
    fontSize: Typography.xs,
  },
  creatorBtns: {
    flexDirection: 'row',
    gap: Spacing.sm,
  },
  joinBtn: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderRadius: Radius.sm,
    paddingVertical: 9,
    alignItems: 'center',
  },
  joinedBtn: {
    backgroundColor: '#26D0CE',
  },
  joinBtnText: {
    color: '#0a0e17',
    fontSize: Typography.sm,
    fontWeight: Typography.bold,
  },
  joinedBtnText: {
    color: '#ffffff',
  },
  openBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: Radius.sm,
    paddingVertical: 9,
    gap: 5,
  },
  openBtnText: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: Typography.sm,
    fontWeight: Typography.medium,
  },

  // Empty state
  emptyWrap: {
    flex: 1,
    paddingTop: Spacing.xxl,
    paddingHorizontal: Spacing.base,
  },
  listEmptyWrapIOS: {
    // FlatList already owns the page gutter. A second inset here makes the
    // iOS content frame resolve wider than its visible scroll-content bounds.
    paddingHorizontal: 0,
  },
  emptyCard: {
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 2,
    borderColor: 'rgba(255,255,255,0.10)',
    borderStyle: 'dashed',
    borderRadius: Radius.xxl,
    padding: Spacing.xxl,
    alignItems: 'center',
    gap: Spacing.md,
    overflow: 'hidden',
  },
  emptyCardIOS: {
    // The list already owns the available width. Let Yoga shrink this frame
    // inside that width rather than retaining the pre-inset iOS measurement.
    overflow: 'visible',
    width: '100%',
    minWidth: 0,
  },
  emptyCardGradient: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    borderRadius: Radius.xxl,
  },
  emptyTopHighlight: {
    position: 'absolute',
    left: 22,
    right: 22,
    top: 1,
    height: 1,
    borderRadius: 1,
    backgroundColor: 'transparent',
  },
  emptyTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.md,
    fontWeight: Typography.semibold,
    textAlign: 'center',
    alignSelf: 'stretch',
    flexShrink: 1,
    marginTop: Spacing.sm,
  },
  emptySubtitle: {
    color: Colors.textMuted,
    fontSize: Typography.sm,
    textAlign: 'center',
    alignSelf: 'stretch',
    flexShrink: 1,
    lineHeight: 20,
  },
  profilePhotoRequirement: {
    minHeight: 38,
    borderRadius: Radius.pill,
    borderWidth: 1,
    borderColor: 'rgba(38,208,206,0.24)',
    backgroundColor: 'rgba(38,208,206,0.08)',
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
  },
  profilePhotoRequirementText: {
    color: Colors.turquoise,
    fontSize: Typography.sm,
    fontWeight: Typography.semibold,
    textAlign: 'center',
  },
  createBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.sm,
    backgroundColor: Colors.sunset,
    borderRadius: Radius.md,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.lg,
    marginTop: Spacing.sm,
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.45,
    shadowRadius: 12,
    elevation: 8,
    overflow: 'hidden',
  },
  createBtnText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },

  // Safety modal
  safetyOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: Spacing.base,
  },
  safetyCard: {
    width: '100%',
    backgroundColor: '#0f1520',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: Radius.xxl,
    padding: Spacing.xl,
    gap: Spacing.md,
  },
  safetyTitle: {
    color: Colors.textPrimary,
    fontSize: Typography.lg,
    fontWeight: Typography.bold,
  },
  safetyPlanName: {
    color: 'rgba(255,255,255,0.65)',
    fontSize: Typography.base,
    lineHeight: 22,
  },
  safetyWarning: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: Spacing.sm,
    backgroundColor: 'rgba(255,255,255,0.04)',
    borderWidth: 1,
    borderRadius: Radius.md,
    padding: Spacing.md,
  },
  safetyWarningText: {
    flex: 1,
    fontSize: Typography.sm,
    lineHeight: 19,
  },
  ackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.md,
  },
  ackCheckbox: {
    width: 22,
    height: 22,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: 'rgba(255,255,255,0.30)',
    backgroundColor: 'rgba(255,255,255,0.06)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  ackCheckboxChecked: {
    backgroundColor: '#F87171',
    borderColor: '#F87171',
  },
  ackText: {
    flex: 1,
    color: 'rgba(255,255,255,0.70)',
    fontSize: Typography.sm,
    lineHeight: 18,
  },
  requestMsgInput: {
    backgroundColor: 'rgba(255,255,255,0.06)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.12)',
    borderRadius: Radius.md,
    color: '#ffffff',
    fontSize: Typography.sm,
    paddingHorizontal: 14,
    paddingVertical: 10,
    minHeight: 52,
    textAlignVertical: 'top',
  },
  safetyActions: {
    flexDirection: 'row',
    gap: Spacing.sm,
    marginTop: Spacing.sm,
  },
  safetyCancel: {
    flex: 1,
    backgroundColor: 'rgba(255,255,255,0.07)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.14)',
    borderRadius: Radius.md,
    paddingVertical: Spacing.md,
    alignItems: 'center',
  },
  safetyCancelText: {
    color: 'rgba(255,255,255,0.60)',
    fontSize: Typography.base,
    fontWeight: Typography.medium,
  },
  safetyConfirm: {
    flex: 1,
    backgroundColor: Colors.sunset,
    borderRadius: Radius.md,
    paddingVertical: Spacing.md,
    alignItems: 'center',
    shadowColor: Colors.sunset,
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.40,
    shadowRadius: 10,
    elevation: 6,
  },
  safetyConfirmDisabled: {
    backgroundColor: 'rgba(242,125,38,0.35)',
    shadowOpacity: 0,
    elevation: 0,
  },
  safetyConfirmText: {
    color: '#fff',
    fontSize: Typography.base,
    fontWeight: Typography.bold,
  },
});

const createThemeStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: daylightStyle(theme, { backgroundColor: theme.colors.background }),
  textPrimary: daylightStyle(theme, { color: theme.colors.textPrimary }),
  textSecondary: daylightStyle(theme, { color: theme.colors.textSecondary }),
  textMuted: daylightStyle(theme, { color: theme.colors.textMuted }),
  newButton: daylightStyle(theme, {
    backgroundColor: theme.colors.controlBackground,
    borderColor: theme.colors.cardBorder,
    shadowOpacity: 0,
    elevation: 0,
  }),
  newBtnHighlight: daylightStyle(theme, {
    backgroundColor: 'transparent',
  }),
  emptyCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceGlass,
    borderColor: theme.colors.cardBorder,
    shadowOpacity: 0,
    elevation: 0,
  }),
  emptyTopHighlight: daylightStyle(theme, {
    backgroundColor: 'transparent',
  }),
  profilePhotoRequirement: daylightStyle(theme, {
    backgroundColor: theme.colors.accentSecondarySoft,
    borderColor: theme.colors.accentSecondaryBorder,
  }),
  profilePhotoRequirementText: daylightStyle(theme, {
    color: theme.colors.chipSelectedText,
  }),
  profilePhotoSupportingText: daylightStyle(theme, {
    color: theme.colors.textSecondary,
  }),
  modalCard: daylightStyle(theme, {
    backgroundColor: theme.colors.surfaceElevated,
    borderColor: theme.colors.border,
    ...theme.shadows.elevated,
  }),
  input: daylightStyle(theme, {
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.border,
    color: theme.colors.textPrimary,
  }),
  secondaryButton: daylightStyle(theme, {
    backgroundColor: theme.colors.chipBackground,
    borderColor: theme.colors.border,
  }),
});

export default PlansScreen;
